import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import { formatPrice } from "../../lib/utils";

const formatDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

/**
 * SHUWA's view of the business wallets: what they are owed against the
 * IntouchPay balance, and withdrawals IntouchPay never answered. Those stay
 * out of the shop's wallet until someone checks the transfer in IntouchPay
 * and records here whether the money arrived.
 */
export default function Wallets() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(null); // { id, status }
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get("/admin/wallets")
      .then(({ data }) => setData(data))
      .catch(() => setError("Could not load the wallets. Please refresh."));

  useEffect(() => {
    load();
  }, []);

  const resolve = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/admin/wallet-withdrawals/${confirming.id}/resolve`, { status: confirming.status });
      setConfirming(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "Could not update this withdrawal. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <p className="text-sm text-muted-foreground">{error || "Loading…"}</p>;

  const check = data.reconciliation;
  const withdrawals = data.pending_withdrawals;
  const shuwa = data.shuwa;
  const businesses = data.businesses;

  return (
    <div>
      <h1 className="font-montserrat text-2xl font-bold text-slate-900">Wallets</h1>
      <p className="mb-6 mt-1 text-sm text-muted-foreground">
        Online payments sit in SHUWA's IntouchPay account: SHUWA's wallet holds its commissions and subscriptions, and each business's and customer's wallet tracks its share.
      </p>

      {error && <p className="mb-4 text-sm text-destructive">{error}</p>}

      <div className="mb-8 grid gap-3 sm:grid-cols-3">
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Owed to business and customer wallets</p>
          <p className="mt-1 font-montserrat text-xl font-extrabold text-slate-900">{formatPrice(check.owed)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">IntouchPay balance</p>
          <p className="mt-1 font-montserrat text-xl font-extrabold text-slate-900">
            {check.intouch_balance === null ? "Unavailable" : formatPrice(check.intouch_balance)}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
          <div className="mt-2">
            {check.covered === null ? (
              <Badge variant="default">Not checked</Badge>
            ) : check.covered ? (
              <Badge variant="accent">Balance covers all wallets</Badge>
            ) : (
              <Badge variant="warning">Short by {formatPrice(check.owed - check.intouch_balance)}</Badge>
            )}
          </div>
        </Card>
      </div>

      {check.overdrawn > 0 && (
        <Card className="mb-8 border-amber-300 bg-amber-50 p-4">
          <p className="font-semibold text-slate-900">
            {check.overdrawn_wallets.length} wallet{check.overdrawn_wallets.length === 1 ? " is" : "s are"} below zero —{" "}
            {formatPrice(check.overdrawn)} in all
          </p>
          <p className="mt-1 text-sm text-slate-700">
            A refund took back money that had already been withdrawn. Future earnings fill the gap automatically; recover
            it from the owner if they stop trading. This amount is not counted in what the wallets are owed.
          </p>
          <ul className="mt-2 text-sm text-slate-800">
            {check.overdrawn_wallets.map((w) => (
              <li key={w.id}>
                {w.business?.name || w.user?.full_name || "Unknown account"}: {formatPrice(w.balance)}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <h2 className="mb-1 font-montserrat text-lg font-bold text-slate-900">SHUWA wallet</h2>
      <p className="mb-3 text-sm text-muted-foreground">
        SHUWA's own share of the IntouchPay account: commissions (booking fees, withdrawal fees and paid statements)
        and SHUWA Pro subscriptions. A booking fee is on hold until its appointment is completed.
      </p>
      <div className="mb-4 grid gap-3 sm:grid-cols-4">
        {[
          ["Balance", shuwa.balance],
          ["On hold", shuwa.pending_balance],
          ["Commissions earned", shuwa.commissions],
          ["Subscriptions earned", shuwa.subscriptions],
        ].map(([label, value]) => (
          <Card key={label} className="p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className="mt-1 font-montserrat text-xl font-extrabold text-slate-900">{formatPrice(value)}</p>
          </Card>
        ))}
      </div>
      {shuwa.transactions.length > 0 && (
        <Card className="mb-8 p-4">
          <p className="text-sm font-semibold text-slate-900">Recent activity</p>
          <ul className="mt-2 divide-y divide-border text-sm">
            {shuwa.transactions.map((tx) => (
              <li key={tx.id} className="flex items-center justify-between gap-3 py-2">
                <div>
                  <p className="text-slate-800">{tx.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {tx.status === "pending" ? "On hold until the appointment is completed" : formatDateTime(tx.available_at || tx.created_at)}
                  </p>
                </div>
                <span className={Number(tx.amount) < 0 ? "text-slate-700" : "font-semibold text-emerald-700"}>
                  {Number(tx.amount) < 0 ? "− " : "+ "}
                  {formatPrice(Math.abs(Number(tx.amount)))}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <h2 className="mb-1 font-montserrat text-lg font-bold text-slate-900">Business sub-wallets</h2>
      <p className="mb-3 text-sm text-muted-foreground">
        Every business has its own sub-wallet. The balance can be withdrawn; money on hold is released when each
        appointment is completed.
      </p>
      {businesses.length === 0 ? (
        <p className="mb-8 text-sm text-muted-foreground">No businesses yet.</p>
      ) : (
        <Card className="mb-8 overflow-x-auto p-4">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                <th className="pb-2 font-medium">Business</th>
                <th className="pb-2 text-right font-medium">Balance</th>
                <th className="pb-2 text-right font-medium">On hold</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {businesses.map((w) => (
                <tr key={w.id}>
                  <td className="py-2 text-slate-800">
                    {w.business?.name || "Unknown business"}
                    {w.business?.deleted_at && <span className="ml-2 text-xs text-muted-foreground">(deleted)</span>}
                  </td>
                  <td className={`py-2 text-right font-semibold ${Number(w.balance) < 0 ? "text-red-600" : "text-slate-900"}`}>
                    {formatPrice(w.balance)}
                  </td>
                  <td className="py-2 text-right text-slate-700">{formatPrice(w.pending_balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <h2 className="mb-1 font-montserrat text-lg font-bold text-slate-900">Withdrawals to check</h2>
      <p className="mb-3 text-sm text-muted-foreground">
        IntouchPay did not answer these transfers, so the money may or may not have been sent. Look each one up in
        IntouchPay by its reference, then record the outcome.
        {check.stale_withdrawals > 0 && (
          <span className="font-semibold text-amber-700">
            {" "}
            {check.stale_withdrawals} {check.stale_withdrawals === 1 ? "has" : "have"} been waiting over an hour.
          </span>
        )}
      </p>

      {withdrawals.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing to check right now.</p>
      ) : (
        <div className="space-y-3">
          {withdrawals.map((w) => (
            <Card key={w.id} className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-900">{w.wallet?.business?.name || w.wallet?.user?.full_name || "Unknown account"}</p>
                  <p className="mt-1 text-sm text-slate-700">
                    {formatPrice(w.payout_amount)} to {w.phone}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatPrice(w.amount)} left the wallet (includes {formatPrice(w.fee)} SHUWA withdrawal fee) ·{" "}
                    {formatDateTime(w.created_at)}
                  </p>
                  <p className="text-xs text-muted-foreground">IntouchPay reference: {w.provider_transaction_id || "none"}</p>
                </div>
                <Badge variant="warning">Pending</Badge>
              </div>

              {confirming?.id === w.id ? (
                <div className="mt-3 rounded-xl border border-border bg-muted/30 p-3">
                  <p className="text-sm text-slate-800">
                    {confirming.status === "success"
                      ? `Confirm that ${formatPrice(w.payout_amount)} reached ${w.phone}. The withdrawal is closed and the wallet stays as it is.`
                      : `Confirm that nothing was sent. ${formatPrice(w.amount)} goes back into the wallet.`}{" "}
                    This cannot be undone.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button onClick={resolve} disabled={busy}>
                      {busy ? "Saving…" : "Confirm"}
                    </Button>
                    <Button variant="outline" onClick={() => setConfirming(null)} disabled={busy}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button onClick={() => setConfirming({ id: w.id, status: "success" })}>Money arrived</Button>
                  <Button variant="outline" onClick={() => setConfirming({ id: w.id, status: "failed" })}>
                    Not sent — return to wallet
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
